import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import {
  HiOutlineChatAlt2,
  HiOutlineRefresh,
  HiOutlineXCircle,
  HiOutlinePhone,
  HiOutlinePaperAirplane,
  HiOutlineInformationCircle,
  HiOutlineLink,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlinePlay,
  HiOutlineDocumentText,
  HiOutlineClipboardList,
} from 'react-icons/hi';
import { settingsApi } from '../../services/api';
import { useClinic } from '../../context/ClinicContext';
import { formatCalendarDateDMY } from '../../utils/timezoneDisplay';

const STATUS_LABELS = {
  disconnected: 'Disconnected',
  connecting: 'Connecting…',
  qr: 'Scan QR code',
  pairing: 'Pairing…',
  connected: 'Connected',
};

const POLL_MS_ACTIVE = 1000;
const POLL_MS_IDLE = 3000;

function statusBadgeClass(state) {
  if (state === 'connected') return 'bg-emerald-500/95 text-white ring-2 ring-emerald-300/50';
  if (state === 'qr' || state === 'connecting' || state === 'pairing') {
    return 'bg-amber-500/95 text-white ring-2 ring-amber-200/50';
  }
  return 'bg-slate-500/95 text-white ring-2 ring-slate-300/50';
}

function formatDisplayPhone(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+91 ${digits.slice(2)}`;
  }
  return digits ? `+${digits}` : '';
}

function isPendingState(state) {
  return state === 'connecting' || state === 'qr' || state === 'pairing';
}

function formatTime12h(time24) {
  if (!time24) return '—';
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr || '00';
  if (Number.isNaN(h)) return time24;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

const EMPTY_AUTOMATION = {
  enabled: false,
  time: '09:00',
  message: '',
  last_run_date: null,
  last_sent: 0,
  last_failed: 0,
};

const TABS = [
  { id: 'connection', label: 'Connection', icon: HiOutlineLink },
  { id: 'test', label: 'Test message', icon: HiOutlinePaperAirplane },
  { id: 'templates', label: 'Templates', icon: HiOutlineDocumentText },
  { id: 'automations', label: 'Automations', icon: HiOutlineClock },
];

const AUTOMATION_SUB_TABS = [
  { id: 'configure', label: 'Configure' },
  { id: 'logs', label: 'Activity logs' },
];

const AUTOMATION_TYPE_LABELS = {
  patient_birthday: 'Patient birthday',
  staff_birthday: 'Staff birthday',
  payment_receipt: 'Payment receipt',
  invoice: 'Invoice',
  test: 'Test message',
};

const WhatsAppSettings = () => {
  const { currentClinic, currentClinicId } = useClinic();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [status, setStatus] = useState({
    state: 'disconnected',
    phone: null,
    last_connected_at: null,
    last_error: null,
  });
  const [qr, setQr] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Hello from Clinic CRM — WhatsApp test message.');
  const [templates, setTemplates] = useState({ payment_receipt: '', invoice: '' });
  const [templateVars, setTemplateVars] = useState([]);
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [automations, setAutomations] = useState({
    patient: { ...EMPTY_AUTOMATION },
    staff: { ...EMPTY_AUTOMATION },
  });
  const [automationVars, setAutomationVars] = useState({ patient: [], staff: [] });
  const [savingAutomations, setSavingAutomations] = useState(false);
  const [runningPatientBday, setRunningPatientBday] = useState(false);
  const [runningStaffBday, setRunningStaffBday] = useState(false);
  const [automationSubTab, setAutomationSubTab] = useState('configure');
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logDays, setLogDays] = useState(30);
  const [logTypeFilter, setLogTypeFilter] = useState('');
  const [logPagination, setLogPagination] = useState({ total: 0, page: 1, totalPages: 0 });
  const [activeTab, setActiveTab] = useState('connection');
  const pollRef = useRef(null);
  const prevStateRef = useRef('disconnected');

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const applyStatus = useCallback((data) => {
    setStatus(data);
    setQr(data.qr || null);

    if (prevStateRef.current !== 'connected' && data.state === 'connected') {
      toast.success(
        data.phone
          ? `WhatsApp connected (${formatDisplayPhone(data.phone)})`
          : 'WhatsApp connected successfully'
      );
    }
    prevStateRef.current = data.state;
    return data;
  }, []);

  const refreshAll = useCallback(async () => {
    const res = await settingsApi.getWhatsAppStatus();
    const data = res.data?.data || {};
    return applyStatus(data);
  }, [applyStatus]);

  const startPolling = useCallback(
    (fast = true) => {
      stopPolling();
      const interval = fast ? POLL_MS_ACTIVE : POLL_MS_IDLE;
      pollRef.current = setInterval(() => {
        refreshAll().catch(() => {});
      }, interval);
    },
    [refreshAll, stopPolling]
  );

  const initialLoad = useCallback(async () => {
    setLoading(true);
    try {
      const data = await refreshAll();
      try {
        const tplRes = await settingsApi.getWhatsAppMessages();
        const tplData = tplRes.data?.data || {};
        setTemplates({
          payment_receipt: tplData.payment_receipt || '',
          invoice: tplData.invoice || '',
        });
        setTemplateVars(tplData.variables || []);
      } catch (tplErr) {
        console.error(tplErr);
      }
      try {
        const autoRes = await settingsApi.getWhatsAppBirthdayAutomations();
        const autoData = autoRes.data?.data || {};
        setAutomations({
          patient: { ...EMPTY_AUTOMATION, ...(autoData.patient || {}) },
          staff: { ...EMPTY_AUTOMATION, ...(autoData.staff || {}) },
        });
        setAutomationVars(autoData.variables || { patient: [], staff: [] });
      } catch (autoErr) {
        console.error(autoErr);
      }
      if (isPendingState(data.state)) {
        startPolling(true);
      } else if (data.state === 'connected') {
        startPolling(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load WhatsApp settings');
    } finally {
      setLoading(false);
    }
  }, [refreshAll, startPolling]);

  const loadActivityLogs = useCallback(async (days = logDays, type = logTypeFilter, page = 1) => {
    setLogsLoading(true);
    try {
      const res = await settingsApi.getWhatsAppAutomationLogs({
        days,
        ...(type ? { automation_type: type } : {}),
        page,
        limit: 50,
      });
      const data = res.data?.data || {};
      setActivityLogs(data.logs || []);
      setLogPagination(data.pagination || { total: 0, page: 1, totalPages: 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load activity logs');
      setActivityLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [logDays, logTypeFilter]);

  useEffect(() => {
    if (activeTab === 'automations' && automationSubTab === 'logs') {
      loadActivityLogs(logDays, logTypeFilter, 1);
    }
  }, [activeTab, automationSubTab, logDays, logTypeFilter, loadActivityLogs]);

  useEffect(() => {
    prevStateRef.current = 'disconnected';
    initialLoad();
    return () => stopPolling();
  }, [currentClinicId, initialLoad, stopPolling]);

  useEffect(() => {
    if (isPendingState(status.state)) {
      startPolling(true);
      return;
    }
    if (status.state === 'connected') {
      startPolling(false);
      return;
    }
    stopPolling();
  }, [status.state, startPolling, stopPolling]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await settingsApi.connectWhatsApp();
      toast.success(res.data?.message || 'QR code ready — scan with WhatsApp');
      const data = await refreshAll();
      if (isPendingState(data.state)) {
        startPolling(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to start connection');
    } finally {
      setConnecting(false);
    }
  };

  const handleReconnect = async () => {
    if (!window.confirm('Generate a new QR code? This will restart the WhatsApp connection for this clinic.')) {
      return;
    }
    setConnecting(true);
    try {
      const res = await settingsApi.connectWhatsApp({ force_new: true });
      toast.success(res.data?.message || 'New QR code generated');
      const data = await refreshAll();
      if (isPendingState(data.state)) {
        startPolling(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reconnect');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect WhatsApp for this clinic? You will need to scan QR again to reconnect.')) {
      return;
    }
    setDisconnecting(true);
    try {
      await settingsApi.disconnectWhatsApp();
      toast.success('WhatsApp disconnected');
      setQr(null);
      prevStateRef.current = 'disconnected';
      await refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    const digits = String(testPhone).replace(/\D/g, '');
    const message = testMessage.trim();
    if (!digits || digits.length < 10) {
      toast.error('Enter a valid phone number (at least 10 digits)');
      return;
    }
    if (!message) {
      toast.error('Enter a test message');
      return;
    }
    setSendingTest(true);
    try {
      await settingsApi.sendWhatsAppTest({ to: digits, message });
      toast.success('Test message sent successfully');
      if (activeTab === 'automations' && automationSubTab === 'logs') {
        loadActivityLogs(logDays, logTypeFilter, 1);
      }
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to send test message';
      toast.error(msg);
    } finally {
      setSendingTest(false);
    }
  };

  const handleSaveTemplates = async (e) => {
    e.preventDefault();
    setSavingTemplates(true);
    try {
      const res = await settingsApi.updateWhatsAppMessages(templates);
      const data = res.data?.data || {};
      setTemplates({
        payment_receipt: data.payment_receipt || templates.payment_receipt,
        invoice: data.invoice || templates.invoice,
      });
      toast.success('Message templates saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save templates');
    } finally {
      setSavingTemplates(false);
    }
  };

  const handleSaveAutomations = async (e) => {
    e.preventDefault();
    setSavingAutomations(true);
    try {
      const res = await settingsApi.updateWhatsAppBirthdayAutomations({
        patient: automations.patient,
        staff: automations.staff,
      });
      const data = res.data?.data || {};
      setAutomations({
        patient: { ...EMPTY_AUTOMATION, ...(data.patient || {}) },
        staff: { ...EMPTY_AUTOMATION, ...(data.staff || {}) },
      });
      toast.success('Birthday automations saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save automations');
    } finally {
      setSavingAutomations(false);
    }
  };

  const handleRunBirthdayNow = async (target) => {
    const label = target === 'patient' ? 'patient' : 'staff';
    if (
      !window.confirm(
        `Send ${label} birthday WhatsApp messages now for everyone with a birthday today?`
      )
    ) {
      return;
    }
    const setRunning = target === 'patient' ? setRunningPatientBday : setRunningStaffBday;
    setRunning(true);
    try {
      const res = await settingsApi.runWhatsAppBirthdayAutomationsNow({ target });
      toast.success(res.data?.message || 'Done');
      const autoRes = await settingsApi.getWhatsAppBirthdayAutomations();
      const autoData = autoRes.data?.data || {};
      setAutomations({
        patient: { ...EMPTY_AUTOMATION, ...(autoData.patient || {}) },
        staff: { ...EMPTY_AUTOMATION, ...(autoData.staff || {}) },
      });
      if (automationSubTab === 'logs') {
        loadActivityLogs(logDays, logTypeFilter, 1);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const updateAutomation = (kind, field, value) => {
    setAutomations((prev) => ({
      ...prev,
      [kind]: { ...prev[kind], [field]: value },
    }));
  };

  const isConnected = status.state === 'connected';
  const isPending = isPendingState(status.state);
  const showQr = status.state === 'qr' && qr;
  const showPairing = status.state === 'pairing' || (status.state === 'connecting' && !qr && !isConnected);

  if (loading) {
    return (
      <div className="w-full min-h-[40vh] flex flex-col items-center justify-center gap-2">
        <div className="h-9 w-9 rounded-full border-2 border-[#425F97] border-t-transparent animate-spin" />
        <p className="text-xs text-gray-500">Loading WhatsApp settings…</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 pb-4 animate-fade-in">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#128C7E] via-[#075E54] to-slate-900 shadow-lg">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15) 0%, transparent 45%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.08) 0%, transparent 40%)',
          }}
        />
        <div className="relative px-4 py-5 md:px-6 md:py-6 lg:flex lg:items-end lg:justify-between lg:gap-4">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white/90 ring-1 ring-white/20 mb-2">
              <HiOutlineChatAlt2 className="w-3.5 h-3.5" />
              Configuration
            </div>
            <h1 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">
              WhatsApp integration
            </h1>
            <p className="mt-1.5 text-xs md:text-sm text-white/85 leading-relaxed">
              Connect, configure templates, and manage automations for{' '}
              <strong>{currentClinic?.name || 'this clinic'}</strong>.
            </p>
          </div>
          <div className="mt-3 lg:mt-0 shrink-0">
            <div
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold shadow ${statusBadgeClass(status.state)}`}
            >
              <span
                className={`h-2 w-2 rounded-full bg-white ${
                  isConnected || isPending ? 'animate-pulse' : ''
                }`}
              />
              {STATUS_LABELS[status.state] || status.state}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto rounded-xl bg-gray-100 p-1.5 w-full sm:w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all sm:px-4 ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200/80'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <tab.icon className="h-4 w-4 shrink-0" />
            <span>{tab.label}</span>
            {tab.id === 'connection' && isConnected ? (
              <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
            ) : null}
            {tab.id === 'automations' &&
            (automations.patient.enabled || automations.staff.enabled) ? (
              <span className="rounded-full bg-pink-100 px-1.5 py-0.5 text-[10px] font-semibold text-pink-700">
                On
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {activeTab === 'connection' && (
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 lg:gap-4 items-start">
        <div className="xl:col-span-4 space-y-3">
          <div className="rounded-xl border border-gray-200/90 bg-white p-4 shadow-sm">
            <div className="flex items-start gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#25D366] text-white shadow-sm">
                <HiOutlineChatAlt2 className="w-4 h-4" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Per-clinic connection</h2>
                <p className="mt-1 text-xs text-gray-600 leading-relaxed">
                  Each clinic can connect its own WhatsApp number. Switch clinic in the header to
                  configure another location.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200/90 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-900 mb-3">
              <HiOutlineLink className="w-4 h-4 text-[#128C7E]" />
              Connection details
            </div>
            <div className="space-y-2 text-xs text-gray-700">
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Clinic</p>
                <p className="mt-1 font-medium text-gray-900">{currentClinic?.name || '—'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Phone</p>
                <p className="mt-1 font-mono text-gray-900">
                  {isConnected && status.phone
                    ? formatDisplayPhone(status.phone)
                    : isPending
                      ? 'Waiting for connection…'
                      : 'Not connected'}
                </p>
              </div>
              {status.last_connected_at && (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                    Last connected
                  </p>
                  <p className="mt-1 text-gray-900">
                    {new Date(status.last_connected_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-amber-100 bg-amber-50/80 p-4 shadow-sm">
            <div className="flex gap-2.5">
              <HiOutlineInformationCircle className="w-4 h-4 shrink-0 text-amber-700 mt-0.5" />
              <div className="text-xs text-amber-950/90">
                <p className="font-semibold text-amber-900">How to connect</p>
                <ol className="mt-1.5 list-decimal list-inside space-y-1 text-amber-900/85">
                  <li>Click Connect WhatsApp</li>
                  <li>Open WhatsApp on your phone → Linked devices</li>
                  <li>Scan the QR code shown here</li>
                  <li>Wait for status to show Connected</li>
                  <li>Open the <strong>Test message</strong> tab to confirm</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="xl:col-span-8 space-y-4">
          <div className="rounded-xl border border-gray-200/90 bg-white p-4 md:p-5 lg:p-6 shadow-sm ring-1 ring-black/[0.03]">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
              <div>
                <h2 className="text-base font-bold text-gray-900">Connection</h2>
                <p className="text-xs text-gray-500">Generate QR and link this clinic&apos;s WhatsApp</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!isConnected && (
                  <button
                    type="button"
                    onClick={handleConnect}
                    disabled={connecting || isPending}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#128C7E] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-[#0d6e63] disabled:opacity-60"
                  >
                    <HiOutlineRefresh className={`w-4 h-4 ${connecting ? 'animate-spin' : ''}`} />
                    {connecting ? 'Starting…' : 'Connect WhatsApp'}
                  </button>
                )}
                {(isConnected || isPending) && (
                  <button
                    type="button"
                    onClick={handleReconnect}
                    disabled={connecting}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    <HiOutlineRefresh className={`w-4 h-4 ${connecting ? 'animate-spin' : ''}`} />
                    New QR
                  </button>
                )}
                {isConnected && (
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                  >
                    <HiOutlineXCircle className="w-4 h-4" />
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                )}
              </div>
            </div>

            {status.last_error && status.state === 'disconnected' && (
              <p className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                {status.last_error}
              </p>
            )}

            {showQr ? (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-inner">
                  <QRCodeSVG value={qr} size={240} level="M" includeMargin />
                </div>
                <p className="mt-4 text-sm font-medium text-gray-800">Scan with WhatsApp mobile app</p>
                <p className="mt-1 text-xs text-gray-500">
                  Open WhatsApp → Settings → Linked devices → Link a device
                </p>
              </div>
            ) : showPairing ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="h-12 w-12 rounded-full border-2 border-[#128C7E] border-t-transparent animate-spin" />
                <p className="mt-4 text-sm font-semibold text-gray-800">
                  {status.state === 'pairing' ? 'Pairing with WhatsApp…' : 'Finishing connection…'}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  QR scanned — please wait a few seconds. Status updates automatically.
                </p>
              </div>
            ) : isConnected ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-10 text-center">
                <HiOutlineCheckCircle className="mx-auto w-12 h-12 text-emerald-600" />
                <p className="mt-3 text-sm font-semibold text-emerald-900">WhatsApp is connected</p>
                {status.phone && (
                  <p className="mt-1 text-sm text-emerald-800 font-mono">
                    {formatDisplayPhone(status.phone)}
                  </p>
                )}
                <p className="mt-2 text-xs text-emerald-700">
                  Open the <strong>Test message</strong> tab to verify sending works.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
                <HiOutlineChatAlt2 className="mx-auto w-10 h-10 text-gray-300" />
                <p className="mt-3 text-sm font-medium text-gray-700">
                  Click Connect WhatsApp to generate a QR code
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {activeTab === 'test' && (
        <form
          onSubmit={handleSendTest}
          className="w-full rounded-xl border border-gray-200/90 bg-white p-4 md:p-5 lg:p-6 shadow-sm ring-1 ring-black/[0.03]"
        >
            <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-100">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#128C7E]/10 text-[#128C7E]">
                <HiOutlinePaperAirplane className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Test message</h2>
                <p className="text-xs text-gray-500">
                  {isConnected
                    ? 'Send a message to verify WhatsApp is working'
                    : 'Connect WhatsApp first to enable test messages'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-gray-800 mb-1.5">
                  Phone number
                </label>
                <div className="relative">
                  <HiOutlinePhone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    disabled={!isConnected}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/50 py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-[#128C7E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#128C7E]/25 disabled:opacity-60"
                    placeholder="10-digit mobile number"
                  />
                </div>
                <p className="mt-1 text-[11px] text-gray-500">India numbers: enter 10 digits (91 added automatically)</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-800 mb-1.5">Message</label>
                <textarea
                  rows={4}
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  disabled={!isConnected}
                  maxLength={1000}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-[#128C7E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#128C7E]/25 disabled:opacity-60 resize-none"
                  placeholder="Type a test message"
                />
              </div>

              <div className="lg:col-span-2">
              <button
                type="submit"
                disabled={!isConnected || sendingTest}
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-[#128C7E] px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-[#0d6e63] focus:outline-none focus:ring-2 focus:ring-[#128C7E] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <HiOutlinePaperAirplane className="h-4 w-4" />
                {sendingTest ? 'Sending… (may take a few seconds)' : 'Send test message'}
              </button>
              </div>
            </div>
        </form>
      )}

      {activeTab === 'templates' && (
        <form
          onSubmit={handleSaveTemplates}
          className="w-full rounded-xl border border-gray-200/90 bg-white p-4 md:p-5 lg:p-6 shadow-sm ring-1 ring-black/[0.03]"
        >
            <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-100">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#128C7E]/10 text-[#128C7E]">
                <HiOutlineChatAlt2 className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Message templates</h2>
                <p className="text-xs text-gray-500">
                  Used when sending from Payments and Invoices pages
                </p>
              </div>
            </div>

            {templateVars.length > 0 && (
              <p className="mb-4 text-[11px] text-gray-500">
                Variables:{' '}
                {templateVars.map((v) => (
                  <code key={v} className="mx-0.5 rounded bg-gray-100 px-1 py-0.5 text-[10px] text-gray-700">
                    {`{{${v}}}`}
                  </code>
                ))}
              </p>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="flex min-h-0 flex-col">
                <label className="block text-xs font-semibold text-gray-800 mb-1.5">
                  Payment receipt (Payments page)
                </label>
                <textarea
                  rows={10}
                  value={templates.payment_receipt}
                  onChange={(e) =>
                    setTemplates((prev) => ({ ...prev, payment_receipt: e.target.value }))
                  }
                  className="min-h-[220px] w-full flex-1 rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-900 font-mono text-[13px] leading-relaxed transition focus:border-[#128C7E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#128C7E]/25 resize-y"
                />
              </div>

              <div className="flex min-h-0 flex-col">
                <label className="block text-xs font-semibold text-gray-800 mb-1.5">
                  Invoice (Invoices page)
                </label>
                <textarea
                  rows={10}
                  value={templates.invoice}
                  onChange={(e) =>
                    setTemplates((prev) => ({ ...prev, invoice: e.target.value }))
                  }
                  className="min-h-[220px] w-full flex-1 rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 text-sm text-gray-900 font-mono text-[13px] leading-relaxed transition focus:border-[#128C7E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#128C7E]/25 resize-y"
                />
              </div>
            </div>

            <div className="mt-4 pt-2">
              <button
                type="submit"
                disabled={savingTemplates}
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-[#128C7E] px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-[#0d6e63] focus:outline-none focus:ring-2 focus:ring-[#128C7E] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingTemplates ? 'Saving…' : 'Save templates'}
              </button>
            </div>
        </form>
      )}

      {activeTab === 'automations' && (
        <div className="w-full space-y-4">
          <div className="flex gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1 w-fit">
            {AUTOMATION_SUB_TABS.map((sub) => (
              <button
                key={sub.id}
                type="button"
                onClick={() => setAutomationSubTab(sub.id)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  automationSubTab === sub.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {sub.label}
              </button>
            ))}
          </div>

          {automationSubTab === 'configure' && (
        <form
          onSubmit={handleSaveAutomations}
          className="w-full rounded-xl border border-gray-200/90 bg-white p-4 md:p-5 lg:p-6 shadow-sm ring-1 ring-black/[0.03]"
        >
            <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-100">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-pink-500/10 text-pink-600">
                <HiOutlineClock className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Birthday automations</h2>
                <p className="text-xs text-gray-500">
                  Auto-send WhatsApp wishes daily at the configured time (IST). Requires WhatsApp
                  connected for this clinic.
                </p>
              </div>
            </div>

            {!isConnected && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                WhatsApp is not connected. Connect in the <strong>Connection</strong> tab before
                scheduled or manual birthday sends will work.
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {['patient', 'staff'].map((kind) => {
                const block = automations[kind];
                const vars = automationVars[kind] || [];
                const running = kind === 'patient' ? runningPatientBday : runningStaffBday;
                const title = kind === 'patient' ? 'Patient birthdays' : 'Staff birthdays';
                return (
                  <div
                    key={kind}
                    className="flex min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50/40 p-4 space-y-3"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(block.enabled)}
                          onChange={(e) => updateAutomation(kind, 'enabled', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-[#128C7E] focus:ring-[#128C7E]"
                        />
                        <span className="text-xs font-medium text-gray-700">Active</span>
                      </label>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-semibold text-gray-700 shrink-0">Send at</label>
                        <input
                          type="time"
                          value={block.time || '09:00'}
                          onChange={(e) => updateAutomation(kind, 'time', e.target.value)}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#128C7E] focus:outline-none focus:ring-2 focus:ring-[#128C7E]/25"
                        />
                        <span className="text-[11px] text-gray-500">({formatTime12h(block.time)} IST)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRunBirthdayNow(kind)}
                        disabled={!isConnected || running}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <HiOutlinePlay className="w-3.5 h-3.5" />
                        {running ? 'Running…' : 'Run now'}
                      </button>
                    </div>

                    {vars.length > 0 && (
                      <p className="text-[11px] text-gray-500">
                        Variables:{' '}
                        {vars.map((v) => (
                          <code
                            key={v}
                            className="mx-0.5 rounded bg-gray-100 px-1 py-0.5 text-[10px] text-gray-700"
                          >
                            {`{{${v}}}`}
                          </code>
                        ))}
                      </p>
                    )}

                    <textarea
                      rows={8}
                      value={block.message || ''}
                      onChange={(e) => updateAutomation(kind, 'message', e.target.value)}
                      className="min-h-[180px] w-full flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 font-mono text-[13px] leading-relaxed transition focus:border-[#128C7E] focus:outline-none focus:ring-2 focus:ring-[#128C7E]/25 resize-y"
                    />

                    {block.last_run_date ? (
                      <p className="text-[11px] text-gray-500">
                        Last run: {block.last_run_date} · Sent {block.last_sent || 0}, failed{' '}
                        {block.last_failed || 0}
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-400">Not run yet</p>
                    )}
                  </div>
                );
              })}

            </div>

            <div className="mt-4 pt-2">
              <button
                type="submit"
                disabled={savingAutomations}
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-[#128C7E] px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-[#0d6e63] focus:outline-none focus:ring-2 focus:ring-[#128C7E] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingAutomations ? 'Saving…' : 'Save automations'}
              </button>
            </div>
        </form>
          )}

          {automationSubTab === 'logs' && (
            <div className="w-full rounded-xl border border-gray-200/90 bg-white shadow-sm ring-1 ring-black/[0.03] overflow-hidden">
              <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <HiOutlineClipboardList className="h-5 w-5 text-[#128C7E]" />
                  <div>
                    <h2 className="text-base font-bold text-gray-900">WhatsApp activity logs</h2>
                    <p className="text-xs text-gray-500">
                      Birthdays, invoices, payments, and test messages sent from this clinic
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={logTypeFilter}
                    onChange={(e) => setLogTypeFilter(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                    aria-label="Filter by type"
                  >
                    <option value="">All types</option>
                    {Object.entries(AUTOMATION_TYPE_LABELS).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={logDays}
                    onChange={(e) => setLogDays(parseInt(e.target.value, 10))}
                    className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                    aria-label="History period"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => loadActivityLogs(logDays, logTypeFilter, logPagination.page)}
                    disabled={logsLoading}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <HiOutlineRefresh className={`h-4 w-4 ${logsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              {logsLoading ? (
                <div className="flex h-48 items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#128C7E] border-t-transparent" />
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="px-6 py-12 text-center text-sm text-gray-500">
                  No WhatsApp activity in this period. Logs appear after birthday automations, manual
                  sends from Payments/Invoices, or test messages.
                </div>
              ) : (
                <div className="max-h-[min(60vh,560px)] w-full overflow-y-auto overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 shadow-[0_1px_0_0_rgb(229,231,235)]">
                      <tr>
                        <th className="px-4 py-3">Date & time</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Recipient</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Sent by</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {activityLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50/80">
                          <td className="px-4 py-3 whitespace-nowrap text-gray-800">
                            {log.sent_at || '—'}
                            {log.run_date ? (
                              <p className="text-[10px] text-gray-400">
                                Run: {formatCalendarDateDMY(log.run_date)}
                              </p>
                            ) : null}
                          </td>
                          <td className="px-4 py-3">
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              {AUTOMATION_TYPE_LABELS[log.automation_type] || log.automation_type}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{log.recipient_name}</p>
                            <p className="text-[10px] capitalize text-gray-400">{log.recipient_type}</p>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-700">
                            {log.phone
                              ? formatDisplayPhone(
                                  log.phone.length === 10 ? `91${log.phone}` : log.phone
                                )
                              : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                log.status === 'sent'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {log.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">{log.sent_by || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {logPagination.totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
                  <span>
                    Page {logPagination.page} of {logPagination.totalPages} ({logPagination.total}{' '}
                    total)
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={logPagination.page <= 1 || logsLoading}
                      onClick={() => loadActivityLogs(logDays, logTypeFilter, logPagination.page - 1)}
                      className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={logPagination.page >= logPagination.totalPages || logsLoading}
                      onClick={() => loadActivityLogs(logDays, logTypeFilter, logPagination.page + 1)}
                      className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppSettings;
